import ActionButton from 'components/ActionButton';
import React, { useEffect, useState } from 'react';
import styles from './index.module.scss';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { Error } from 'components/message/Error';
import { LOCALE, SALES_CHANNEL_TYPES, showError, showSuccess } from 'utils';
import { createSalesChannel } from 'services/api/salesChannel';
import DropDown from 'components/FormFields/dropdown';
import ToggleSwitch from 'components/ToggleSwitch';

const validationSchema = yup.object().shape({
  name: yup.string().required('Please enter name'),
  description: yup.string().required('Please enter description'),
  email: yup.string().email().required('Please enter email'),
  phone: yup.string().required('Please enter phone'),
  apiProvider: yup.string().required('Please enter apiProvider'),
  username: yup.string().typeError('username must be a string')
  .when('apiProvider', {
    is: SALES_CHANNEL_TYPES.DOORDASH,
    then: yup.string().required('Please enter username'),
  }),
  host: yup.string().typeError('host must be a string')
  .when('apiProvider', {
    is: SALES_CHANNEL_TYPES.DOORDASH,
    then: yup.string().required('Please enter host'),
  }),
  accessToken: yup.string().required('Please enter accessToken'),
  storeId: yup.string().required('Please enter storeId'),
  inventoryItemsFilter: yup
    .string()
    .test('data-validation', 'Please enter a valid JSON', async (value) => {
      try {
        JSON.parse(value || '{}');
      } catch (e) {
        return false;
      }
      return true;
    }),
  markupPercent: yup.number().typeError('markupPercent must be a number'),
  uberClientId: yup
    .string()
    .typeError('uberClientId must be string')
    .when('apiProvider', {
      is: SALES_CHANNEL_TYPES.UBER,
      then: yup.string().required('Please enter uberClientId'),
    }),
  uberClientSecret: yup
    .string()
    .typeError('uberClientSecret must be string')
    .when('apiProvider', {
      is: SALES_CHANNEL_TYPES.UBER,
      then: yup.string().required('Please enter uberClientSecret'),
    }),
  stockThreshold: yup.number().typeError('stockThreshold must be a number'),
  useBackgroundLessImages: yup
    .boolean()
    .typeError('useBackgroundLessImages must be a boolean'),
  active: yup
    .boolean()
    .typeError('active must be a boolean'),
});

type FormData = {
  name: string;
  description: string;
  username: string;
  host: string;
  email: string;
  phone: string;
  inventoryItemsFilter: string;
  accessToken: string;
  storeId: string;
  apiProvider: string;
  markupPercent: number;
  uberClientSecret: string;
  uberClientId: string;
  stockThreshold: number;
  useBackgroundLessImages: boolean;
  active: boolean;
};

const CreateSalesChannel = (props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [showUberCreds, setShowUberCreds] = useState(false);
  const [useBackgroundLessImages, setUseBackgroundLessImages] = useState(false);
  const [active, setActive] = useState(true);

  const { register, handleSubmit, errors, setValue } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    register('useBackgroundLessImages');
    register('active');
    setValue('active', true);
  }, []);

  const saveSalesChannelFn = handleSubmit(async (values) => {
    setLoading(true);
    try {
      await createSalesChannel({
        ...values,
        inventoryItemsFilter: values?.inventoryItemsFilter
          ? JSON.parse(values.inventoryItemsFilter)
          : {},
        stockThreshold: values?.stockThreshold ?? 0,
      });
      showSuccess('Sales Channel created successfully.');
      props.history.goBack();
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  });

  const getHeader = () => {
    return (
      <div className={styles['header']}>
        <div className={styles['heading']}>Create a New Sales Channel</div>
      </div>
    );
  };

  const getFooter = () => {
    return (
      <div className={styles['footer']}>
        <ActionButton
          text={'Cancel'}
          active={!loading}
          isLoading={false}
          onClick={() => props.history.goBack()}
          customClass={styles['cancel']}
        />
        <ActionButton
          text={'Save'}
          active={!loading}
          isLoading={loading}
          onClick={saveSalesChannelFn}
        />
      </div>
    );
  };

  const getFieldError = (error) => {
    if (!error) return null;
    return <Error message={error} />;
  };

  const getName = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Name*</div>
        <input
          name={'name'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.name?.message)}
      </div>
    );
  };

  const getUsername = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Username</div>
        <input
          name={'username'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.username?.message)}
      </div>
    );
  };

  const getHost = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Host</div>
        <input
          name={'host'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.host?.message)}
      </div>
    );
  };

  const getDescription = () => {
    return (
      <div className={styles['description']}>
        <div className={styles['label']}>Description*</div>
        <textarea
          name={'description'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.description?.message)}
      </div>
    );
  };

  const getEmail = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Email*</div>
        <input
          name={'email'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.email?.message)}
      </div>
    );
  };

  const getPhone = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Phone*</div>
        <input
          name={'phone'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.phone?.message)}
      </div>
    );
  };

  const getInventoryItemsFilter = () => {
    return (
      <div className={styles['description']}>
        <div className={styles['label']}>Inventory Items Filter</div>
        <textarea
          name={'inventoryItemsFilter'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.inventoryItemsFilter?.message)}
      </div>
    );
  };

  const getAccessToken = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Access Token*</div>
        <input
          name={'accessToken'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        <span className={styles['note']}>
          Note: If provider does not have persisted token then provide any dummy
          value
        </span>
        {getFieldError(errors?.accessToken?.message)}
      </div>
    );
  };

  const getStoreId = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Store Id*</div>
        <input
          name={'storeId'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.storeId?.message)}
      </div>
    );
  };

  const getApiPrividersArray = Object.keys(SALES_CHANNEL_TYPES)?.map((item) => {
    return { value: SALES_CHANNEL_TYPES[item], name: item };
  });

  const getApiProvider = () => {
    return (
      <DropDown
        title="Api Provider*"
        selectInputProps={{
          disabled: loading,
          ref: register,
          name: 'apiProvider',
          className: styles['select-input'],
        }}
        onChange={(e) => {
          setShowUberCreds(e === SALES_CHANNEL_TYPES.UBER);
        }}
        options={getApiPrividersArray}
      />
    );
  };

  const getLocalesArray = Object.keys(LOCALE)?.map((item) => {
    return { value: item, name: LOCALE[item] };
  });

  const getLocale = () => {
    return (
      <DropDown
        title="Locale"
        selectInputProps={{
          disabled: loading,
          ref: register,
          name: 'locale',
          className: styles['select-input'],
        }}
        options={getLocalesArray}
        onChange={() => {}}
      />
    );
  };

  const getMarkupPercentage = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Markup Percentage</div>
        <input
          name={'markupPercent'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.markupPercent?.message)}
      </div>
    );
  };

  const getUberCLientId = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Uber Client Id</div>
        <input
          name={'uberClientId'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.uberClientId?.message)}
      </div>
    );
  };

  const getUberCLientSecret = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Uber Client Secret</div>
        <input
          name={'uberClientSecret'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.uberClientSecret?.message)}
      </div>
    );
  };

  const getUberCreds = () => {
    if (!showUberCreds) return null;
    return (
      <>
        {getUberCLientId()}
        {getUberCLientSecret()}
      </>
    );
  };

  const getThresholdQuantity = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Threshold Quantity</div>
        <input
          name={'stockThreshold'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.stockThreshold?.message)}
      </div>
    );
  };

  const getUseBackgroundLessImages = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Use backgroundless images</div>
        <ToggleSwitch
          switchLabel=""
          disabled={loading}
          checked={useBackgroundLessImages}
          onChange={(e) => {
            setUseBackgroundLessImages(e);
            setValue('useBackgroundLessImages', e);
          }}
        />
      </div>
    );
  };

  const getActiveToggle = () => {
    return (
      <div className={`${styles['name']} ml-3`}>
        <div className={styles['label']}>Active</div>
        <ToggleSwitch
          switchLabel=""
          disabled={loading}
          checked={active}
          onChange={(e) => {
            setActive(e);
            setValue('active', e);
          }}
        />
      </div>
    );
  };

  return (
    <div className={styles['main-container']}>
      {getHeader()}
      <div className={styles['sub-container']}>
        <div className={styles['item-details']}>
          {getName()}
          {getDescription()}
          {getUsername()}
          {getHost()}
          {getEmail()}
          {getPhone()}
          {getMarkupPercentage()}
          {getInventoryItemsFilter()}
          {getStoreId()}
          {getApiProvider()}
          {getLocale()}
          {getUberCreds()}
          {getAccessToken()}
          {getThresholdQuantity()}
          <div className='d-flex'>
            {getUseBackgroundLessImages()}
            {getActiveToggle()}
          </div>
        </div>
      </div>
      {getFooter()}
    </div>
  );
};

export { CreateSalesChannel };
